import { Loader } from '@googlemaps/js-api-loader';

export type GooglePlacesLocationOption = {
  cityState: string;
  label: string;
  value: string;
};

export type LocationAnswerAddressComponents = {
  city: string;
  country_code: string;
  country_name: string;
  latitude: number;
  longitude: number;
  postal_code: string;
  state_province_code: string;
  state_province_name: string;
  street_number_and_name: string;
};

export type LocationAnswer = {
  address_components?: LocationAnswerAddressComponents;
  formatted_address?: string;
  place_id?: string;
  raw_address: string;
  validation_status: 'unvalidated' | 'validated';
};

type GooglePlacesComponentRestrictions = {
  country: string[] | string;
};

type FormattableText = { text: string };
type PlacePrediction = {
  placeId: string;
  secondaryText?: FormattableText;
  text?: FormattableText;
};
type AutocompleteSuggestion = { placePrediction?: PlacePrediction };
type AddressComponent = { longText: string; shortText: string; types: string[] };
type PlaceInstance = {
  addressComponents?: AddressComponent[];
  fetchFields(request: { fields: string[] }): Promise<void>;
  formattedAddress?: string;
  id: string;
  location?: { lat: () => number; lng: () => number };
};
type PlaceConstructor = new (options: { id: string }) => PlaceInstance;
type PlacesLibrary = {
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(request: AutocompleteRequest): Promise<{
      suggestions: AutocompleteSuggestion[];
    }>;
  };
  Place: PlaceConstructor;
};
type AutocompleteRequest = {
  includedPrimaryTypes?: string[];
  includedRegionCodes?: string[];
  input: string;
};

const getComponentsBySpec = (
  components: AddressComponent[] | undefined,
  spec: readonly (readonly [resultKey: string, googleType: string, useShort?: boolean])[]
): Record<string, string> => {
  const acc: Record<string, string> = {};
  if (!components?.length) return acc;
  for (const component of components) {
    const typeSet = new Set(component.types);
    for (const [resultKey, googleType, useShort = false] of spec) {
      if (resultKey in acc) continue;
      if (!typeSet.has(googleType)) continue;
      const key = useShort ? 'shortText' : 'longText';
      acc[resultKey] = component[key] ?? '';
    }
  }
  return acc;
};

let loaderInstance: Loader | null = null;
let loadPromise: Promise<PlacesLibrary> | null = null;
let loadedApiKey: string | null = null;

export const ensureGoogleMapsPlacesLoaded = (
  apiKey: string
): Promise<PlacesLibrary> => {
  if (typeof window === 'undefined') {
    throw new Error('Google Maps can only be loaded in a browser environment');
  }

  if (!apiKey) {
    throw new Error('Missing Google Maps API key');
  }

  if (loadPromise && loadedApiKey === apiKey) {
    return loadPromise;
  }

  loadedApiKey = apiKey;
  loaderInstance = new Loader({
    apiKey,
    version: 'weekly',
    libraries: ['places'],
  });
  loadPromise = loaderInstance.load().then(() =>
    loaderInstance!.importLibrary('places') as Promise<PlacesLibrary>
  );
  return loadPromise;
};

export const fetchGooglePlacesLocationOptions = async ({
  apiKey,
  componentRestrictions,
  input,
  types,
}: {
  apiKey: string;
  componentRestrictions?: GooglePlacesComponentRestrictions;
  input: string;
  types?: string[];
}): Promise<GooglePlacesLocationOption[]> => {
  const { AutocompleteSuggestion } = await ensureGoogleMapsPlacesLoaded(apiKey);

  const includedRegionCodes = componentRestrictions?.country
    ? Array.isArray(componentRestrictions.country)
      ? componentRestrictions.country
      : [componentRestrictions.country]
    : undefined;

  const request: AutocompleteRequest = {
    input,
    includedPrimaryTypes: types,
    includedRegionCodes,
  };
  const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

  return (suggestions ?? []).map((s) => {
    const p = s.placePrediction;
    const text = p?.text?.text ?? '';
    const secondaryText = p?.secondaryText?.text ?? '';
    return {
      cityState: secondaryText,
      label: text,
      value: p?.placeId ?? text,
    };
  });
};

export const createGooglePlacesLocationLoadOptions = ({
  apiKey,
  country,
  types,
}: {
  apiKey: string;
  country?: string[] | string;
  types?: string[];
}) =>
  async (value: string): Promise<GooglePlacesLocationOption[]> => {
    const input = value.replace(/\s+/gu, ' ').trim();
    if (!input) return [];
    if (!apiKey) return [];
    try {
      return await fetchGooglePlacesLocationOptions({
        apiKey,
        input,
        types,
        componentRestrictions: country ? { country } : undefined,
      });
    } catch {
      return [];
    }
  };

const MIN_PLACE_ID_LENGTH = 27;
const PLACE_ID_REGEX = /^[A-Za-z0-9_-]+$/u; // e.g. ChIJN1t_tDeuEmsRUsoyG83frY4

const isValidPlaceId = (id: string): boolean => {
  const trimmed = id?.trim() ?? '';
  return (
    trimmed.length >= MIN_PLACE_ID_LENGTH &&
    !trimmed.includes(' ') &&
    PLACE_ID_REGEX.test(trimmed)
  );
};

export const fetchGooglePlacesDetails = async ({
  apiKey,
  placeId,
  rawAddress,
}: {
  apiKey: string;
  placeId: string;
  rawAddress: string;
}): Promise<LocationAnswer | null> => {
  if (!isValidPlaceId(placeId)) {
    return null;
  }
  const library = await ensureGoogleMapsPlacesLoaded(apiKey);
  const { Place } = library;
  const place = new Place({ id: placeId });
  await place.fetchFields({
    fields: ['formattedAddress', 'addressComponents', 'location', 'id'],
  });
  const formatted = place.formattedAddress ?? rawAddress;
  const componentsMap = getComponentsBySpec(place.addressComponents, [
    ['street_number', 'street_number'],
    ['route', 'route'],
    ['locality', 'locality'],
    ['sublocality_level_1', 'sublocality_level_1'],
    ['state_province_code', 'administrative_area_level_1', true],
    ['state_province_name', 'administrative_area_level_1'],
    ['postal_code', 'postal_code'],
    ['country_code', 'country', true],
    ['country_name', 'country'],
  ]);
  const street_number_and_name = [componentsMap.street_number, componentsMap.route]
    .filter(Boolean)
    .join(' ');
  const city =
    componentsMap.locality || componentsMap.sublocality_level_1;
  const lat = place.location?.lat?.() ?? 0;
  const lng = place.location?.lng?.() ?? 0;
  return {
    raw_address: rawAddress,
    formatted_address: formatted,
    address_components: {
      street_number_and_name,
      city,
      state_province_code: componentsMap.state_province_code,
      state_province_name: componentsMap.state_province_name,
      postal_code: componentsMap.postal_code,
      country_code: componentsMap.country_code,
      country_name: componentsMap.country_name,
      latitude: lat,
      longitude: lng,
    },
    validation_status: 'validated',
    place_id: place.id ?? placeId,
  };
};
